'use client';

/**
 * Presence — tells the server a game is open, for as long as it is.
 *
 * The play counter (lib/gamePlays.ts) fires once per sitting, ten seconds in,
 * and that is all it should do: it picks favourites. It cannot say whether the
 * person is still in the game twenty minutes later. The console's Games ▸ Now
 * playing page needs exactly that, so the open game sends a heartbeat:
 *
 *   on    when the game opens, then every BEAT_MS while the tab is in front
 *   off   when the game closes or the page goes away (keepalive, so it lands)
 *
 * A hidden tab stops beating and drops off the page after three missed beats.
 * That is deliberate: a game in a background tab is paused, not being played.
 *
 * Signed-in only (the server refuses guests), best effort, and silent: a failed
 * beat costs one row on an operator's page, never anything the player sees.
 */

import { useEffect } from 'react';

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';
const BEAT_MS = 25_000;

function beat(game: string, state: 'on' | 'off') {
  try {
    void fetch(`${API}/api/games/presence`, {
      method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game, state }),
    }).catch(() => { /* best effort */ });
  } catch { /* best effort */ }
}

/** Hold presence in `game` while this component is mounted and the tab is in front. */
export function useGamePresence(game: string, signedIn: boolean): void {
  useEffect(() => {
    if (!signedIn) return;
    const tick = () => { if (document.visibilityState === 'visible') beat(game, 'on'); };
    tick();
    const timer = setInterval(tick, BEAT_MS);
    // Coming back to the tab should show up at once, not at the next beat.
    document.addEventListener('visibilitychange', tick);
    const leave = () => beat(game, 'off');
    window.addEventListener('pagehide', leave);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [game, signedIn]);
}
